/**
 * The strings an operator copies out of this panel and into something else.
 *
 * Three of them: the WebSocket URL an installer pastes into a charger's
 * configuration screen, the id-and-password pair that goes into a message to
 * whoever is on site, and the pay-ui address behind a driver's QR sticker.
 *
 * ## Why this is a module and not four template literals
 *
 * Every value here leaves the application. Nothing downstream validates it: a
 * charger given a malformed URL fails its handshake with an error that names
 * neither side, and a QR code pointing at the wrong path is discovered by a driver
 * who cannot start a charge. So the assembly is a pure function with tests against
 * the cases that actually bite — a trailing slash on a configured origin, an origin
 * that was never configured at all, a deployment that never declared one.
 *
 * ## The two halves of the address come from different places
 *
 * The scheme, host and path prefix are the *deployment's* (`GET /deployment`, see
 * `lib/deployment.ts`); the id and password are the *station's*. Neither can be
 * derived from the other, which is why every function here takes the base URL as
 * an argument that may be absent rather than reading it from a module-level
 * constant.
 */

export interface StationCredentialInput {
  /**
   * `wss://host/ocpp` from `GET /deployment`, or `null`/`undefined` when the
   * deployment has not declared where it lives.
   */
  readonly ocppBaseUrl: string | null | undefined;
  readonly chargePointId: string;
  /** The plaintext, which exists in this browser tab and nowhere else. */
  readonly password: string;
}

/**
 * `wss://{id}:{password}@{host}/ocpp/{id}` — the whole address, credentials
 * included, or `undefined` when the deployment's own origin is unknown.
 *
 * **The id appears twice and that is not redundancy.** Spec 3.1 has the station's
 * id as the last path segment, and spec 9 has the same id as the HTTP Basic
 * username; the server checks that the two agree and refuses the connection when
 * they do not.
 *
 * **`user:password@`, not `user@password:`.** Userinfo is RFC 3986's, and a
 * charger parses it with a URL library rather than by reading a runbook.
 *
 * There is deliberately no fallback to the browser's own origin. A panel reached at
 * `localhost:5180` through a dev proxy would print `wss://localhost:5180/ocpp` —
 * an address that is wrong in a way an operator cannot see, on the one string they
 * are about to configure a physical charger with. Absent is better than plausible.
 */
export function ocppUrl(input: StationCredentialInput): string | undefined {
  if (input.ocppBaseUrl === null || input.ocppBaseUrl === undefined) return undefined;

  const base = withoutTrailingSlash(input.ocppBaseUrl);
  const separator = base.indexOf("://");
  if (separator === -1) return undefined;

  const scheme = base.slice(0, separator);
  const authorityAndPath = base.slice(separator + "://".length);

  /**
   * Percent-encoded, both times, and kept that way now that it is belt-and-braces.
   *
   * A station id can no longer contain anything that needs escaping: the server's
   * charset is `[A-Za-z0-9._-]`, enforced at every gate and by a CHECK on the
   * column, and every one of those characters is unreserved in RFC 3986. The
   * encoding stays because this function's output is configured into a physical
   * charger by hand — if the charset ever widens, the failure without it is a URL
   * that parses as a different station rather than one that fails to parse. The
   * path segment is encoded for the same reason the server decodes it
   * (`parseChargePointId`).
   */
  const id = encodeURIComponent(input.chargePointId);
  const password = encodeURIComponent(input.password);

  return `${scheme}://${id}:${password}@${authorityAndPath}/${id}`;
}

/**
 * `{id}:{password}` — the credential as one token, which is how it is written in a
 * message to the person at the charger.
 *
 * **The separator is a colon because a station id cannot contain one.** RFC 7617
 * builds the Basic header as `base64(username + ":" + password)`, which is exactly
 * why the server refuses a colon in an id — a receiver must split on the first one
 * — and that refusal is what makes this token unambiguous here. The pair reads the
 * way every charger's configuration screen and every HTTP tool writes it, rather
 * than in a notation local to this panel.
 *
 * Unescaped on purpose: this is read by a human and typed into two separate fields,
 * so a `%3A` in it would be transcribed literally into the username box.
 */
export function ocppCredential(chargePointId: string, password: string): string {
  return `${chargePointId}:${password}`;
}

/**
 * Both lines, labelled — what one press of the panel's copy button puts on the
 * clipboard, ready to paste into a chat window whole.
 *
 * The whole point is that the operator does not retype anything. A sysadmin
 * transcribing a URL and a credential into a message gets one of them wrong often
 * enough that this button is the feature, not the convenience.
 *
 * With no configured origin the URL line is dropped rather than printed broken; the
 * credential line is still worth having, and it is the half that cannot be
 * reconstructed later.
 */
export function credentialBlock(input: StationCredentialInput): string {
  const url = ocppUrl(input);
  const credential = `OCPP Credential: ${ocppCredential(input.chargePointId, input.password)}`;

  return url === undefined ? credential : `OCPP URL: ${url}\n${credential}`;
}

export interface PayUiUrlInput {
  /** The pay-ui origin from `GET /deployment`, or absent with no payment rail. */
  readonly payUiBaseUrl: string | null | undefined;
  readonly chargePointId: string;
  /**
   * Omitted for the station-level page, which is what a charger whose bays are
   * not separately labelled gets. pay-ui routes both (`[cpId]/index.vue`,
   * `[cpId]/[evseId].vue`).
   *
   * An EVSE — the bay a car charges at — not a gun on it: a driver pays for a
   * bay, and on a 1.6 station the two numbers are the same
   * (docs/ocpp-201-spec.md D2). Must be a real bay. EVSE 0 is refused — see
   * the function.
   */
  readonly evseId?: number;
}

/**
 * **EVSE 0 is the charge point itself, not a bay** — 2.0.1's EVSE 0, and what a
 * 1.6 station calls connector 0 (OCPP 1.6 §4.4).
 *
 * A station reports it alongside its real bays, and the server drops it from
 * the offerable list in the query rather than afterwards — `publicCatalogRepository`
 * says why: "leaving it in the list and hoping every consumer drops it is how it
 * eventually reaches a picker." This is that consumer, and it drops it too.
 */
const FIRST_SELECTABLE_EVSE = 1;

/**
 * Where a driver's phone lands after scanning a sticker, or `undefined` for a
 * deployment with no payment rail — which is what a fleet-only depot is, and it has
 * no sticker to print.
 *
 * The id is percent-encoded: a slash in it would invent a path segment and point
 * the sticker at some other station's page. The server's own id rules forbid a
 * slash, and this does not depend on that staying true.
 *
 * EVSE 0 gets `undefined` rather than a URL. pay-ui answers a 404 for it, so
 * the alternative is a sticker that scans perfectly and lands a driver on an error
 * page — the worst available failure, because nothing about the code looks wrong.
 */
export function payUiUrl(input: PayUiUrlInput): string | undefined {
  if (input.payUiBaseUrl === null || input.payUiBaseUrl === undefined) return undefined;
  if (input.evseId !== undefined && input.evseId < FIRST_SELECTABLE_EVSE) {
    return undefined;
  }

  const base = withoutTrailingSlash(input.payUiBaseUrl);
  const station = `${base}/${encodeURIComponent(input.chargePointId)}`;

  return input.evseId === undefined ? station : `${station}/${input.evseId}`;
}

function withoutTrailingSlash(url: string): string {
  return url.replace(/\/+$/, "");
}
